import getActiveComp from 'common/src/modules/creativeTemplates/copilot/agents/adBuilderAgent/utils/getActiveComp';
import updateActiveComp from 'common/src/modules/creativeTemplates/store/creativeTemplatePage/formatMovies/actions/updateActiveComp';
import updateLayer from 'common/src/modules/creativeTemplates/store/creativeTemplatePage/formatMovies/actions/updateLayer';
import {LayerWithWorkingProps} from 'common/src/modules/movieEditor/movieEditor.types';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {AdBuilderAgentToolName} from '../adBuilderAgent.enums';

// Schema for updating layer general parameters
const inputSchema = z.object({
  layerId: z.string().describe('The ID of the layer to update'),
  startTime: z.number().nullable().optional().describe('Start time of the layer in milliseconds'),
  endTime: z.number().nullable().optional().describe('End time of the layer in milliseconds'),
  parentLayerId: z
    .string()
    .nullable()
    .optional()
    .describe('The ID of the layer to parent this layer to. Use empty string to remove parenting.'),
  isHidden: z.boolean().nullable().optional().describe('Whether the layer is hidden'),
  isLocked: z.boolean().nullable().optional().describe('Whether the layer is locked'),
  name: z.string().nullable().optional().describe('The new name for the layer'),
  zOrder: z
    .number()
    .nullable()
    .optional()
    .describe(
      'The new z-order (stacking order, 1 is on top) for the layer. value 1 to layerCount.',
    ),
});

function updateLayerGeneralParamsTool(store: CLStore) {
  return tool({
    name: AdBuilderAgentToolName.UpdateLayerGeneralParams,
    displayName: 'Updating layer general parameters',
    description: 'Updates a layer including start/end time, parenting, and enable/disable states.',
    inputSchema,
    execute: async ({input}: ToolExecuteParams<typeof inputSchema>) => {
      const activeComp = getActiveComp(store);

      if (!activeComp) {
        return toolError('No active composition found');
      }

      try {
        // Find the layer to update
        const layer = activeComp.l.find((l) => l.id === input.layerId);

        if (!layer) {
          return toolError(`Layer with ID "${input.layerId}" not found`);
        }

        // Validate zOrder if provided
        if (input.zOrder !== undefined) {
          const totalLayers = activeComp.l.length;

          if ((input.zOrder ?? 0) < 1 || (input.zOrder ?? 0) > totalLayers) {
            return toolError(
              `zOrder must be between 1 and ${totalLayers} (total number of layers)`,
            );
          }
        }

        // Find parent layer if specified
        let parentLayerIndex: number | undefined;

        if (input.parentLayerId !== undefined) {
          if (input.parentLayerId === '') {
            // Remove parenting
            parentLayerIndex = undefined;
          } else {
            const parentLayer = activeComp.l.find((l) => l.id === input.parentLayerId);

            if (!parentLayer) {
              return toolError(`Parent layer with ID "${input.parentLayerId}" not found`);
            }

            parentLayerIndex = parseInt(parentLayer.i);
          }
        }

        // Create update object with only the specified parameters
        const updates: Partial<LayerWithWorkingProps> = {};
        if (input.startTime !== undefined) updates.s = input.startTime ?? 0;
        if (input.endTime !== undefined) updates.e = input.endTime ?? 0;
        if (input.parentLayerId !== undefined) updates.pi = parentLayerIndex;
        if (input.isHidden !== undefined) updates.isHidden = input.isHidden ?? false;
        if (input.isLocked !== undefined) updates.isLocked = input.isLocked ?? false;
        if (input.name !== undefined) updates.name = input.name ?? '';
        //if (input.audioEnabled !== undefined) updates.aEn = input.audioEnabled;

        // Handle zOrder reordering if specified
        if (input.zOrder !== undefined) {
          const currentLayerIndex = parseInt(layer.i);
          const newLayerIndex = input.zOrder ?? 0;

          // Only reorder if the position actually changes
          if (currentLayerIndex !== newLayerIndex) {
            // Step 1: Update the i values for all layers based on the move
            const updatedLayers = activeComp.l.map((l) => {
              const layerI = parseInt(l.i);

              if (layerI === currentLayerIndex) {
                // The layer being moved gets the new position
                return {...l, i: newLayerIndex.toString()};
              } else if (currentLayerIndex < newLayerIndex) {
                // Moving down: layers between current and new position shift up
                if (layerI > currentLayerIndex && layerI <= newLayerIndex) {
                  return {...l, i: (layerI - 1).toString()};
                }
              } else {
                // Moving up: layers between new and current position shift down
                if (layerI >= newLayerIndex && layerI < currentLayerIndex) {
                  return {...l, i: (layerI + 1).toString()};
                }
              }

              // Return unchanged layer
              return l;
            });

            // Step 2: Sort layers by i value in descending order (1 is on top)
            const sortedLayers = updatedLayers.sort((a, b) => parseInt(b.i) - parseInt(a.i));

            // Step 3: Update the composition with the reordered layers
            await store.dispatch(
              updateActiveComp({
                data: {l: sortedLayers},
              }),
            );
          }
        }

        // Update the layer
        await store.dispatch(
          updateLayer({
            layerId: input.layerId,
            data: updates,
          }),
        );

        return toolResult(`Updated layer "${layer.name}" parameters`);
      } catch (error) {
        return toolError(
          `Failed to update layer parameters: ${error instanceof Error ? error.message : 'Unknown error'}`,
        );
      }
    },
  });
}

export default updateLayerGeneralParamsTool;
